/**
 * Utility functions for ranking calculations and data processing
 */

/**
 * Calculates percentage for ranking bar widths
 * @param {number} value - Current value
 * @param {number} maxValue - Maximum value in the dataset
 * @returns {number} Percentage (0-100)
 */
export function calculatePercentage(value, maxValue) {
  if (!maxValue || maxValue === 0) return 0;
  return Math.round((value / maxValue) * 100);
}

/**
 * Processes ranking data for display
 * @param {Array} data - Raw data array
 * @param {number} limit - Maximum number of items to display
 * @returns {Array} Processed data with percentages
 */
export function processRankingData(data, limit = 5) {
  if (!data || !Array.isArray(data)) return [];

  const sortedData = data
    .slice(0, limit)
    .sort((a, b) => b.value - a.value);

  const maxValue = Math.max(...sortedData.map((item) => item.value));

  return sortedData.map((item) => ({
    name: item.name,
    value: item.value,
    percentage: calculatePercentage(item.value, maxValue),
  }));
}

/**
 * Validates ranking data structure
 * @param {Object} item - Data item to validate
 * @returns {boolean} True if valid
 */
export function isValidRankingItem(item) {
  return item &&
         typeof item === 'object' &&
         typeof item.name === 'string' &&
         typeof item.value === 'number' &&
         item.value >= 0;
}

/**
 * Filters and validates ranking data
 * @param {Array} data - Raw data array
 * @param {number} limit - Maximum items to return
 * @returns {Array} Validated and filtered data
 */
export function filterValidRankingData(data, limit = 5) {
  if (!data || !Array.isArray(data)) return [];

  return data
    .filter(isValidRankingItem)
    .slice(0, limit)
    .sort((a, b) => b.value - a.value);
}
